import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'

import { GetActivityHistoryActivityType, GetActivityHistoryResponseType } from '@/shared/backend/types'

import Pagination, { paginate } from 'components/Pagination/Pagination'
import { EventNames, getFiltersFromLocalStorage, getSavEventDetails, saveFiltersToLocalStorage } from './util'
import { EventDetailsDialog } from './EventDetailsDialog'
import { Header } from './Header'
import { Container, EmptyTable } from './styles'
import { SavRow } from './SavRow'
import { VaultDisplayInfo } from 'utils/vaultDisplayInfo'
import { ActivityHistoryContentLoader } from './ActivityHistoryContentLoader'
import { Typography } from 'components/Typography/Typography'

interface Props {
  events?: GetActivityHistoryResponseType
  vaultDisplayInfo: VaultDisplayInfo
  address: string
  isLoading: boolean
}

const PAGE_SIZE = 10

const FILTERS_KEY = 'sav-activity-history-filters'

const defaultFilters = {
  [EventNames.SavDeposit]: true,
  [EventNames.SavWithdraw]: true,
  [EventNames.SavSend]: true,
  [EventNames.SavReceive]: true,
}

const getLsDataKey = (vaultId: string, address: string) => `${vaultId}-${address}`

export const SavActivityHistory = ({ vaultDisplayInfo, address, events, isLoading }: Props) => {
  const [filters, setFilters] = useState<Record<string, boolean>>(() =>
    getFiltersFromLocalStorage(FILTERS_KEY, getLsDataKey(vaultDisplayInfo.vault.id, address), defaultFilters)
  )

  const [currentPage, setCurrentPage] = useState(1)
  const [activeEvent, setActiveEvent] = useState<GetActivityHistoryActivityType>()

  useEffect(() => {
    setCurrentPage(1)
  }, [events])

  useEffect(() => {
    const positionFilters = getFiltersFromLocalStorage(
      FILTERS_KEY,
      getLsDataKey(vaultDisplayInfo.vault.id, address),
      defaultFilters
    )
    setFilters(positionFilters)
  }, [address, vaultDisplayInfo.vault.id])

  const displayEvents = useMemo(() => {
    if (!events) {
      return []
    }
    const selectedEventTypes = Object.keys(filters).filter(key => filters[key] === true)
    return [...events].reverse().filter(e => {
      return selectedEventTypes.indexOf(e.kind) >= 0
    })
  }, [events, filters])

  const handleEventRowClick = useCallback((event: GetActivityHistoryActivityType) => setActiveEvent(event), [])
  const handleCloseEvent = useCallback(() => setActiveEvent(undefined), [])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  const handleFilterClick = useCallback(
    (filterName: keyof typeof filters) => {
      const newFilters = {
        ...filters,
        [filterName]: !filters[filterName],
      }
      setFilters(newFilters)
      saveFiltersToLocalStorage(FILTERS_KEY, getLsDataKey(vaultDisplayInfo.vault.id, address), newFilters)
      setCurrentPage(1)
    },
    [filters, vaultDisplayInfo.vault.id, address]
  )

  const paginatedItems = paginate(displayEvents, PAGE_SIZE, currentPage)

  const totalPages = Math.ceil(displayEvents.length / PAGE_SIZE)

  if (isLoading) {
    return (
      <Container>
        <Header
          filters={filters}
          handleFilterClick={handleFilterClick}
          filterItems={Object.keys(defaultFilters)}
          disabledFilter
        />
        <ActivityHistoryContentLoader />
      </Container>
    )
  }

  return (
    <>
      <Container>
        <Header
          filters={filters}
          handleFilterClick={handleFilterClick}
          filterItems={Object.keys(defaultFilters)}
          disabledFilter={events?.length === 0}
        />
        {paginatedItems?.length === 0 && (
          <EmptyTable>
            <Typography variant="label2" fontWeight="semiBold" color="secondary">
              No events to display
            </Typography>
          </EmptyTable>
        )}
        {paginatedItems.map((event, index) => (
          <SavRow
            key={event.transactionDigest}
            event={event}
            isLast={index === paginatedItems.length - 1}
            handleEventRowClick={handleEventRowClick}
            vaultDisplayInfo={vaultDisplayInfo}
          />
        ))}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          type={`sav-activity-history-${getLsDataKey(vaultDisplayInfo.vault.id, address)}`}
        />
      </Container>
      {activeEvent && (
        <EventDetailsDialog
          kind={activeEvent.kind}
          timestampMs={activeEvent.timestampMs}
          transactionDigest={activeEvent.transactionDigest}
          details={getSavEventDetails(activeEvent, vaultDisplayInfo)}
          onClose={handleCloseEvent}
        />
      )}
    </>
  )
}
